/**
 * What happens outside the window: the badge, and notifications.
 *
 * Both are driven by one thing — the unread counts the sync store already
 * keeps — rather than by the message stream. That is deliberate. The counts
 * are what the server says, they survive a reconnect and a reload, and they
 * are the same numbers the sidebar is showing, so the badge and the sidebar
 * can never disagree. Your own messages never increment those counts, so a
 * send from the phone does not raise a desktop notification on the laptop.
 *
 * It also means a notification never carries a message body. That is a
 * property worth keeping: a preview on a locked screen or a shared desk is a
 * leak that the person being notified did not choose, and relaii's own push
 * gateway takes the same position (`protocol/push-v1.md`).
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { useConnectionState, useSyncStore } from '@/api/store'
import type { ConnectionStatus } from '@/api/store'
import { countsUnread, isMutedRoom, unreadOf } from '@/api/types'
import type { NotificationPreference, Room } from '@/api/types'
import { useRoomLabels, roomLabel } from '@/components/Sidebar'
import { focusDesktopWindow, reportUnread } from '@/lib/desktop'

interface Reading {
  unread: number
  mentions: number
}

/**
 * Whether an arrival in this room is worth interrupting somebody for.
 *
 * The room's own setting wins over the account-wide one. A muted room still
 * wakes for a mention — `client-api.md` says so and the server's push
 * decision does the same — so a phone and a browser agree on what silence
 * means.
 */
export function notificationWanted(
  room: Room,
  preference: NotificationPreference,
  mentioned: boolean,
): boolean {
  const membership = room.my_membership
  if (membership === null) return false
  if (isMutedRoom(room)) return mentioned

  const override = membership.notification ?? 'default'
  if (override === 'mentions') return mentioned
  if (override === 'all') return true

  switch (preference) {
    case 'nothing':
      return false
    // A direct or group conversation is addressed to you as surely as an
    // @mention is; `mentions` keeps only channels quiet.
    case 'mentions':
      return mentioned || room.kind !== 'channel'
    default:
      return true
  }
}

function canNotify(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted'
}

/**
 * Only when the window is not the one being looked at. Notifying somebody
 * about a message they can already see is how a client teaches people to turn
 * notifications off.
 */
function unattended(): boolean {
  if (typeof document === 'undefined') return false
  return document.visibilityState === 'hidden' || !document.hasFocus()
}

export function useActivityBridge(): void {
  const { rooms } = useSyncStore()
  const { status } = useConnectionState()
  const { user, server } = useAuth()
  const labels = useRoomLabels()
  const navigate = useNavigate()

  const previous = useRef<Map<string, Reading> | null>(null)
  const lastStatus = useRef<ConnectionStatus | null>(null)
  // Read inside the effect without making it a dependency: a new label map
  // arrives on every room the sidebar draws, and re-running this then would
  // compare a snapshot against itself.
  const labelsRef = useRef(labels)
  labelsRef.current = labels

  const preference: NotificationPreference = user?.notification_preference ?? 'all'
  const spaceName = server?.display_name?.trim() || server?.server_id || 'relaii'

  // This hook owns the browser title and the desktop badge for as long as it
  // is mounted. When it goes — at sign-out — both return to neutral, or the
  // taskbar would keep showing a count for a Space nobody is signed in to.
  useEffect(() => {
    return () => {
      document.title = 'relaii'
      reportUnread({ unread: 0, mentions: 0 })
    }
  }, [])

  useEffect(() => {
    const current = new Map<string, Reading>()
    let unread = 0
    let mentions = 0

    for (const room of rooms) {
      const state = unreadOf(room)
      current.set(room.id, { unread: state.count, mentions: state.mentions })
      if (!countsUnread(room)) continue
      unread += state.count
      mentions += state.mentions
    }

    reportUnread({ unread, mentions })
    document.title = `${unread > 0 ? `(${unread}) ` : ''}${spaceName} - relaii`

    const before = previous.current
    previous.current = current

    // A catch-up is not an arrival. The first pass is the backlog after a
    // reload; while the line is down or the log is being re-read the counters
    // move in bulk; and the pass in which the connection comes back carries
    // whatever the catch-up found. None of it is worth a notification. The
    // snapshot has moved on regardless, so nothing in that backlog is
    // announced later either.
    const settled = status === 'online' && lastStatus.current === 'online'
    lastStatus.current = status
    if (before === null || !settled || !canNotify() || !unattended()) return

    for (const room of rooms) {
      const seen = before.get(room.id)
      if (seen === undefined) continue

      const now = current.get(room.id)
      if (now === undefined) continue

      const newMentions = now.mentions > seen.mentions
      const newMessages = now.unread > seen.unread
      if (!newMentions && !newMessages) continue
      if (!notificationWanted(room, preference, newMentions)) continue

      const name = roomLabel(room, labelsRef.current)
      const where = room.kind === 'channel' ? `#${name}` : name

      const notification = new Notification(
        newMentions ? `You were mentioned in ${where}` : where,
        {
          body: newMentions ? 'Open relaii to read it.' : 'New messages.',
          // One notification per room replaces the last, rather than stacking
          // twelve of them for one busy channel.
          tag: `relaii-room-${room.id}`,
          silent: false,
        },
      )

      notification.onclick = () => {
        focusDesktopWindow()
        window.focus()
        navigate(`/rooms/${encodeURIComponent(room.id)}`)
        notification.close()
      }
    }
  }, [navigate, preference, rooms, spaceName, status])
}
